/**
 * rateProjection.js — projected points for the /rate report card.
 *
 * Runs entirely CLIENT-SIDE. api/rate-league.js hands the page the league's
 * raw Sleeper `scoring_settings` and nothing else; every number below is
 * computed in the browser from two committed artifacts:
 *   - src/data/defaultProjections2026.js  (season stat lines + games)
 *   - the league's own scoring lens, via sleeperImport's scoringFromSleeper
 * Nothing here touches api/_lib/rateReport.js, which stays pure market math.
 *
 * DATA RULES
 *   1. Stats are truth, points are a view. Season totals go through
 *      scoreFromStats with the league's lens; we never store points.
 *   2. An unmatched player projects as `null`, never 0. A missing projection
 *      and a projected zero are different claims, and the page renders them
 *      differently (a dash vs a number).
 *   3. K AND DEF ARE PROJECTED, AND THEY IGNORE THE LENS (Aug 30 2026). Ben:
 *      "the point projections are off if you don't." A team that starts a
 *      kicker and a defense scores points with them, and leaving two lineup
 *      slots at zero understated every such roster. Their numbers come from
 *      src/data/kdProjections2026.js as finished SEASON POINTS under standard
 *      K and DEF scoring, so rule 1 does not apply to them: there is no stat
 *      line to re-score, and multiplying somebody else's total by a lens would
 *      be an invented figure wearing a real one's clothes. The page says so on
 *      its face. IDP is still not projected at all.
 */

import { DEFAULT_PROJECTIONS } from "../data/defaultProjections2026.js";
import { KD_PROJECTIONS_2026 } from "../data/kdProjections2026.js";
import { normName, posGroup } from "./playerNameNorm.js";
import { scoreFromStats } from "./projlabProjection.js";
import { scoringFromSleeper } from "./sleeperImport.js";

/**
 * Positions the report projects. IDP is not one of them.
 *
 * QB/RB/WR/TE come from the stat-line projection set; K and DEF come from the
 * standard-scoring table (see KD_POSITIONS below). The union is what a lineup
 * slot may be filled from.
 */
export const PROJECTED_POSITIONS = ["QB", "RB", "WR", "TE", "DEF", "K"];

/** The two whose points are a committed total rather than a scored stat line. */
export const KD_POSITIONS = ["DEF", "K"];

/**
 * Position group, extended with team defenses.
 *
 * ⚠️ NOT A CHANGE TO posGroup ITSELF. That normalizer is shared with the
 * nflverse historical-team join, where "team defenses don't have a
 * team-by-season problem (the DEF *is* the team)" is a documented reason for
 * returning null. This is /rate's own widening of it, local to the file that
 * needs it, so the shared rule keeps meaning what it says.
 */
export function projGroup(pos) {
  const p = String(pos || "").toUpperCase();
  if (p === "DEF" || p === "DST" || p === "D/ST") return "DEF";
  return posGroup(pos);
}

/**
 * Sleeper lineup slot -> the position groups that may fill it.
 * SUPER_FLEX is first-class and takes a QB: a second QB in the superflex slot
 * is a starter, not depth, and that is the whole point of the format.
 * Anything absent here (DL, LB, DB, IDP_FLEX, BN, IR, taxi) is a slot we do
 * not project and is skipped entirely.
 *
 * K and DEF take only their own position and are deliberately absent from
 * every flex list: no platform lets a kicker fill a flex, and a superflex is a
 * second quarterback, not a second defense.
 */
export const SLOT_ELIGIBILITY = {
  QB: ["QB"],
  RB: ["RB"],
  WR: ["WR"],
  TE: ["TE"],
  DEF: ["DEF"],
  K: ["K"],
  REC_FLEX: ["WR", "TE"],
  WRRB_FLEX: ["WR", "RB"],
  WR_RB_FLEX: ["WR", "RB"],
  FLEX: ["RB", "WR", "TE"],
  SUPER_FLEX: ["QB", "RB", "WR", "TE"],
};

/**
 * Fill order, narrowest eligibility first. Dedicated slots claim their own
 * position before any flex can take it, and SUPER_FLEX fills last because it
 * accepts everything. (REC_FLEX and WRRB_FLEX overlap on WR without nesting,
 * so greedy is a heuristic rather than a proof in a room running both — no
 * Sleeper preset does, and the two are ordered by width regardless.)
 */
const FILL_ORDER = [
  "QB",
  "RB",
  "WR",
  "TE",
  // Single-eligibility, so they can sit anywhere in the order; here beside the
  // other dedicated slots, ahead of every flex, for the same reason those are.
  "DEF",
  "K",
  "REC_FLEX",
  "WRRB_FLEX",
  "WR_RB_FLEX",
  "FLEX",
  "SUPER_FLEX",
];

// ---------------------------------------------------------------------------
// Name index over DEFAULT_PROJECTIONS

let INDEX = null;

/**
 * `${normName}|${group}` -> { player, pos, games, stats }, built once.
 * Keying on the position group as well as the name keeps a WR and an RB who
 * share a name apart; on a duplicate key the entry with more projected games
 * wins, so a starter beats a same-named backup deterministically.
 */
export function projectionIndex() {
  if (INDEX) return INDEX;
  const idx = new Map();
  for (const roster of Object.values(DEFAULT_PROJECTIONS)) {
    for (const row of roster) {
      const group = posGroup(row.pos);
      // The stat-line set carries the four skill positions only; K and DEF have
      // their own table and never enter this index.
      if (!group || KD_POSITIONS.includes(group) || !PROJECTED_POSITIONS.includes(group)) continue;
      const key = `${normName(row.player)}|${group}`;
      const prev = idx.get(key);
      if (!prev || (row.games || 0) > (prev.games || 0)) idx.set(key, row);
    }
  }
  INDEX = idx;
  return INDEX;
}

/** The key `perPlayer` is addressed by — picks are name+pos, same as the board. */
export function playerKey(name, pos) {
  return `${name}|${String(pos || "").toUpperCase()}`;
}

/**
 * `${normName}|${group}` -> { seasonPts, games } for the 32 defenses and the 32
 * starting kickers, built once from KD_PROJECTIONS_2026.
 *
 * Keyed by NAME rather than by team abbreviation on purpose: the harvest, the
 * board and the entry form all speak names ("Houston Texans", "Cam Little"),
 * and the two sources behind the table disagree about at least one abbreviation
 * (JAC/JAX). A name is the thing every caller actually has.
 */
let KD_INDEX = null;

export function kdIndex() {
  if (KD_INDEX) return KD_INDEX;
  const idx = new Map();
  for (const entry of Object.values(KD_PROJECTIONS_2026)) {
    idx.set(`${normName(entry.def.name)}|DEF`, entry.def);
    idx.set(`${normName(entry.k.name)}|K`, entry.k);
  }
  KD_INDEX = idx;
  return KD_INDEX;
}

/**
 * One player's projection, or null when nothing carries them (rule 2 — a miss
 * is a miss, not a zero). Returns { seasonPts, ppg, games }.
 *
 * 🔴 `lens` REACHES THE FOUR SKILL POSITIONS AND NOTHING ELSE. K and DEF read
 * a finished season total scored under standard K/DEF scoring; see
 * kdProjections2026.js for why re-scoring one against a user's lens would be an
 * invented number. The report says so on its face rather than leaving a reader
 * to notice that their 6-point-per-FG house rules did not move the kicker.
 */
export function projectPlayer(name, pos, lens) {
  const group = projGroup(pos);
  if (!group || !PROJECTED_POSITIONS.includes(group)) return null;

  if (KD_POSITIONS.includes(group)) {
    const row = kdIndex().get(`${normName(name)}|${group}`);
    if (!row) return null;
    const games = Number(row.games) || 0;
    const seasonPts = Number(row.points);
    if (games <= 0 || !Number.isFinite(seasonPts)) return null;
    return { seasonPts, ppg: seasonPts / games, games };
  }

  const row = projectionIndex().get(`${normName(name)}|${group}`);
  if (!row) return null;
  const games = Number(row.games) || 0;
  if (games <= 0) return null;
  const seasonPts = scoreFromStats(row.stats, group, lens, games);
  if (!Number.isFinite(seasonPts)) return null;
  return { seasonPts, ppg: seasonPts / games, games };
}

// ---------------------------------------------------------------------------
// Lineup

/**
 * Greedy optimal lineup: for each lineup slot in FILL_ORDER, take the highest
 * remaining ppg the slot accepts.
 *
 * 🔴 THE LINEUP IS CHOSEN ON PPG AND THE SEASON TOTAL IS READ OFF IT, in that
 * order, deliberately. `season` is the sum of THESE starters' season points —
 * the same eleven names the week 1 number is made of — not a second lineup
 * optimised for the season. A team's best week and its best year are the same
 * roster to a reader, and two lineups on one card would be two different teams
 * wearing one name. The visible consequence: a starter the projection set gives
 * fewer games to carries his full ppg into week 1 and a smaller share into the
 * season, which is the honest reading of both numbers rather than a discrepancy.
 *
 * @param positionCounts { [sleeperSlot]: count } — report.positions, raw
 * @param players        [{ player, pos, ppg, seasonPts }] — matched only
 * @returns { starters: [{ slot, ...player }], week1, season }
 */
export function optimalLineup(positionCounts, players) {
  const counts = positionCounts || {};
  const pool = (players || [])
    .filter((p) => p && Number.isFinite(p.ppg))
    .map((p) => ({ ...p, group: projGroup(p.pos) }))
    .filter((p) => p.group && PROJECTED_POSITIONS.includes(p.group))
    .sort((a, b) => b.ppg - a.ppg);

  const used = new Set();
  const starters = [];
  for (const slot of FILL_ORDER) {
    const n = Number(counts[slot]) || 0;
    if (n <= 0) continue;
    const eligible = SLOT_ELIGIBILITY[slot];
    for (let i = 0; i < n; i += 1) {
      let taken = -1;
      for (let j = 0; j < pool.length; j += 1) {
        if (!used.has(j) && eligible.includes(pool[j].group)) {
          taken = j;
          break;
        }
      }
      if (taken < 0) break;
      used.add(taken);
      starters.push({ slot, ...pool[taken] });
    }
  }
  const week1 = starters.reduce((a, s) => a + s.ppg, 0);
  const season = starters.reduce((a, s) => a + (s.seasonPts || 0), 0);
  return { starters, week1, season };
}

/**
 * How many lineup slots this league starts that the projection set can fill.
 *
 * 🔴 K AND DEF ARE COUNTED NOW, AND THE REASON IS THE SAME ONE THAT USED TO
 * EXCLUDE THEM (changed Aug 30 2026, with the K/DEF projections). This number
 * is the axis /rate's similar-league buckets are cut on, and the quantity being
 * bucketed is projected week 1 points. While a kicker slot contributed exactly
 * zero to that, two rooms differing only in a kicker belonged in one bucket.
 * A kicker now contributes about eight points a week and a defense about six,
 * so a room that starts both has a week 1 total roughly fourteen points above
 * one that starts neither. Bucketing those together would compare lineups of
 * different sizes, which is the exact failure this function exists to prevent.
 *
 * ⚠️ THE COST, STATED. /rate's manual form still has no K or DEF stepper, so a
 * manually described league counts neither and lands in a bucket most real
 * rooms are not in - usually no bucket at all, and then the pooled cell, named
 * as pooled. A saved league carrying real K and DEF counts is now matched
 * BETTER than it was. Giving the manual form those two steppers is the fix and
 * it is not in this change.
 *
 * Exported so the generator and the client cut on ONE definition. Two copies
 * of this rule is how a user gets placed in a bucket built for a different
 * shape.
 */
export function projectedSlotCount(positionCounts) {
  return FILL_ORDER.reduce((a, slot) => a + (Number((positionCounts || {})[slot]) || 0), 0);
}

// ---------------------------------------------------------------------------
// Team + room

/**
 * Project one team's roster.
 *
 * @param picks          [{ player, pos }] — the team's picks off the report
 * @param positionCounts report.positions
 * @param lens           house scoring object (scoringFromSleeper output)
 * @returns { week1, season, starters, perPlayer, matchedCount, pickCount,
 *            starterSlots, starterMatchedCount }
 *   week1                sum of starter ppg
 *   season               sum of the SAME starters' season points
 *   perPlayer            "name|POS" -> { seasonPts, ppg } for matched players
 *   matched              every matched pick, with ppg — the grading input
 *   starterSlots         projectable lineup slots this format defines
 *   starterMatchedCount  how many of those slots a matched player filled
 */
export function projectTeam(picks, positionCounts, lens) {
  const rows = (picks || []).map((p) => ({
    player: p.player,
    pos: p.pos,
    proj: projectPlayer(p.player, p.pos, lens),
  }));

  const perPlayer = {};
  const matched = [];
  for (const r of rows) {
    if (!r.proj) continue;
    perPlayer[playerKey(r.player, r.pos)] = { seasonPts: r.proj.seasonPts, ppg: r.proj.ppg };
    matched.push({ player: r.player, pos: r.pos, ...r.proj });
  }

  const starterSlots = projectedSlotCount(positionCounts);
  const { starters, week1, season } = optimalLineup(positionCounts, matched);

  return {
    week1,
    season,
    starters,
    perPlayer,
    matched,
    matchedCount: matched.length,
    pickCount: rows.length,
    starterSlots,
    starterMatchedCount: starters.length,
  };
}

// ---------------------------------------------------------------------------
// Positional strength

/**
 * The four position groups the card GRADES.
 *
 * 🔴 STILL FOUR AFTER K AND DEF BECAME PROJECTABLE. Grading means placing a
 * score against a corpus cell, and there is no honest K or DEF cell to build:
 * every team in the corpus would be measured on a table with 32 rows and no
 * spread worth a percentile, and the chips would say "thin kicker" about a
 * difference of one point a week. K and DEF are projected, shown, and counted
 * in the lineup; they are not graded, and the report says so where the badge
 * would otherwise be.
 */
export const GRADE_POSITIONS = ["QB", "RB", "WR", "TE"];

/** Tercile words, best first. Fixed vocabulary: the card only ever says these. */
export const GRADE_WORDS = ["strong", "solid", "thin"];

/**
 * One team's depth score at one position: the sum of its top-K matched ppg,
 * where K is that position's DEDICATED slot count plus one — the starters plus
 * a single depth piece.
 *
 * 🔴 K IS UNIFORM ON PURPOSE, INCLUDING IN SUPERFLEX. A room with a SUPER_FLEX
 * slot does not get QB K = QB slots + SFLEX slots + 1: the superflex QB2 counts
 * as the depth piece the +1 already allows. The point of the rule is that every
 * team in the room is measured on the same K at the same position, so the
 * ranking underneath the chips compares like with like. Making K format
 * dependent would change what the chip means between rooms without changing
 * what it says. Do not "fix" this into a format branch without a reason that
 * survives that objection.
 *
 * Flex slots are deliberately excluded from K for the same reason: a FLEX is
 * not a position, and folding it into one position's K would inflate that
 * position for every team that happens to flex there.
 *
 * @returns { score, matched, k } — matched 0 means the room has no projection
 *          for this team at this position, which is not the same as a low score.
 */
export function positionScore(matched, group, positionCounts) {
  const slots = Number((positionCounts || {})[group]) || 0;
  const k = slots + 1;
  const ppgs = (matched || [])
    .filter((m) => projGroup(m.pos) === group && Number.isFinite(m.ppg))
    .map((m) => m.ppg)
    .sort((a, b) => b - a);
  const score = ppgs.slice(0, k).reduce((a, b) => a + b, 0);
  return { score, matched: ppgs.length, k };
}

/**
 * The flex slots, grouped by the card they grade on.
 *
 * 🔴 FLEX AND SFLEX ARE TWO DIFFERENT QUESTIONS AND GET TWO DIFFERENT CELLS.
 * A superflex slot is filled by a quarterback in almost every room that runs
 * one; a flex slot is filled by a running back or a receiver. Pooling them
 * would grade a QB2 against a WR4 and call the comparison a percentile.
 * REC_FLEX, WRRB_FLEX and WR_RB_FLEX are narrower spellings of the same flex
 * idea and share its card, which is also how SLOT_GROUP in rateTeam.js maps
 * them onto the report.
 */
export const FLEX_KINDS = {
  FLEX: ["FLEX", "REC_FLEX", "WRRB_FLEX", "WR_RB_FLEX"],
  SFLEX: ["SUPER_FLEX"],
};

/**
 * One team's depth score at FLEX or at SFLEX: the players the lineup filler
 * actually put in those slots, plus one depth piece, exactly the "starters plus
 * one" shape positionScore uses at a real position.
 *
 * 🔴 THE +1 IS THE BEST REMAINING ELIGIBLE BENCH PLAYER, NOT THE NEXT BEST
 * ANYTHING. A flex card asks "how good is this team at the flex", and the
 * honest answer includes the man who takes the slot on a bye week. He has to be
 * eligible for the slot to count: a spare QB is depth for a superflex and is
 * not depth for a flex, and the eligibility union is taken over the flex slots
 * THIS ROOM ACTUALLY RUNS so a REC_FLEX room does not get credit for a bench
 * running back it could never start there.
 *
 * A room with no slot of this kind returns matched 0 and sits out the cell
 * entirely, the same rule positionScore applies to a position nobody matched:
 * a team that cannot have a flex is not a team with a bad flex.
 *
 * @param proj            projectTeam output (starters carry their slot)
 * @param positionCounts  the room's slot counts, Sleeper slot names
 * @param kind            "FLEX" or "SFLEX"
 * @returns { score, matched, k, slots }
 */
export function flexScore(proj, positionCounts, kind) {
  const counts = positionCounts || {};
  const slotNames = FLEX_KINDS[kind] || [];
  const running = slotNames.filter((slot) => (Number(counts[slot]) || 0) > 0);
  const slots = running.reduce((a, slot) => a + (Number(counts[slot]) || 0), 0);
  if (slots <= 0) return { score: 0, matched: 0, k: 0, slots: 0 };

  const starters = (proj && proj.starters) || [];
  const inSlot = starters
    .filter((s) => running.includes(s.slot) && Number.isFinite(s.ppg))
    .map((s) => s.ppg);

  const eligible = new Set();
  for (const slot of running) for (const group of SLOT_ELIGIBILITY[slot] || []) eligible.add(group);

  const started = new Set(starters.map((s) => `${s.player}|${s.pos}`));
  const depth = ((proj && proj.matched) || [])
    .filter((m) => !started.has(`${m.player}|${m.pos}`))
    .filter((m) => eligible.has(projGroup(m.pos)) && Number.isFinite(m.ppg))
    .map((m) => m.ppg)
    .sort((a, b) => b - a)
    .slice(0, 1);

  const ppgs = [...inSlot, ...depth].sort((a, b) => b - a);
  const k = slots + 1;
  return { score: ppgs.slice(0, k).reduce((a, b) => a + b, 0), matched: ppgs.length, k, slots };
}

// ---------------------------------------------------------------------------
// The roster as a set of position-group cards. BOTH /rate reports draw these.

/**
 * Lineup slot -> the group card it belongs on.
 *
 * Every narrow flex spelling lands on FLEX; SUPER_FLEX gets its own card,
 * because it is graded against its own cell (see FLEX_KINDS). Derived from
 * FLEX_KINDS rather than typed out twice, so the card a slot lands on and the
 * distribution it is measured against cannot disagree.
 */
export const SLOT_GROUP = { QB: "QB", RB: "RB", WR: "WR", TE: "TE", DEF: "DEF", K: "K" };
for (const [group, slots] of Object.entries(FLEX_KINDS)) {
  for (const slot of slots) SLOT_GROUP[slot] = group;
}

/**
 * The cards, in roster order.
 *
 * 🔴 DEF AND K ARE ON IT SINCE THEY BECAME PROJECTABLE. A group missing from
 * this list is a group whose players are silently dropped from the report: the
 * starter loop walks THIS array, not the map it filled. Before Aug 30 that was
 * correct because nothing filled a K or DEF slot; leaving them off afterwards
 * would have shown a projection that counted a kicker in the headline total and
 * then never showed him.
 *
 * Last, and in buildRosterSlots' own DEF-then-K order, so the report reads down
 * the roster the way the ladder was typed.
 */
export const GROUP_ORDER = ["QB", "RB", "WR", "TE", "FLEX", "SFLEX", "DEF", "K"];

/**
 * The roster split into starter rows and bench rows, by group.
 *
 * 🔴 ONE DEFINITION OF "WHO STARTS WHERE", USED BY BOTH REPORTS (round 6).
 * rateTeam's buildGroups grades these against the corpus; the league report
 * ranks them inside one room. Those are two different yardsticks laid over one
 * membership, and the membership is here so the two pages cannot disagree about
 * which card a player appears on.
 *
 * Starters come from the FILLER rather than from the slot anybody typed, so a
 * QB who ends up starting in SUPER_FLEX appears where he actually plays. Bench
 * is simply everyone the filler did not start.
 *
 * @param picks the team's graded picks
 * @param proj  projectTeam output for the same team, under the DISPLAY lens
 * @returns { starterRows, benchRows } — two Maps of group -> rows
 */
export function splitRoster(picks, proj) {
  const all = picks || [];
  const starters = (proj && proj.starters) || [];
  const startedKeys = new Set(starters.map((s) => `${s.player}|${s.pos}`));

  const pickFor = (name, pos) => all.find((p) => p.player === name && p.pos === pos) || null;
  const ppgFor = (name, pos) => {
    const hit = (proj && proj.matched ? proj.matched : []).find(
      (m) => m.player === name && m.pos === pos
    );
    return hit ? hit.ppg : null;
  };

  const starterRows = new Map(GROUP_ORDER.map((g) => [g, []]));
  for (const s of starters) {
    const group = SLOT_GROUP[s.slot] || s.group;
    if (!starterRows.has(group)) starterRows.set(group, []);
    const pick = pickFor(s.player, s.pos);
    starterRows.get(group).push({
      ...(pick || { player: s.player, pos: s.pos }),
      lineupSlot: s.slot,
      ppg: s.ppg,
      seasonPts: s.seasonPts,
    });
  }

  const benchRows = new Map();
  for (const p of all) {
    if (startedKeys.has(`${p.player}|${p.pos}`)) continue;
    const group = projGroup(p.pos) || p.pos;
    if (!benchRows.has(group)) benchRows.set(group, []);
    benchRows.get(group).push({ ...p, ppg: ppgFor(p.player, p.pos) });
  }

  return { starterRows, benchRows };
}

/**
 * The league report's cards: membership only, no badge.
 *
 * The badge on this page is the ROOM rank word, and a room word cannot be
 * decided one team at a time - it is a tercile over every team in the room, so
 * assignPositionGrades computes it and the view reads it off the team. What
 * this function owns is which card each player lands on, and it owns it by
 * calling the same splitRoster the team tab does.
 *
 * 🔴 A GROUP OFF THE END OF GROUP_ORDER IS APPENDED, NOT DROPPED. The team tab
 * cannot produce one - its entry pool only offers rosterable positions - but a
 * real Sleeper room can roster an IDP, and this list replaced a flat pick list
 * that showed every pick. A player who vanished from his own roster would be a
 * strictly worse report than the one this replaces.
 */
export function roomGroups(picks, proj) {
  const { starterRows, benchRows } = splitRoster(picks, proj);
  const cards = (rows, kind) => {
    const out = [];
    const extras = [...rows.keys()].filter((g) => !GROUP_ORDER.includes(g));
    for (const group of [...GROUP_ORDER, ...extras]) {
      const list = rows.get(group);
      // A position with nobody on it simply has no card. Never an empty one.
      if (!list || list.length === 0) continue;
      out.push({ key: group, kind, rows: list, ungraded: KD_POSITIONS.includes(group) });
    }
    return out;
  };
  return { starters: cards(starterRows, "starters"), bench: cards(benchRows, "bench") };
}

/**
 * Rank each position's depth score within the room and map terciles onto
 * GRADE_WORDS. Teams with nothing matched at a position sit out the ranking
 * entirely and carry grade null — the card shows them "no projection", never
 * "thin", because an unmatched roster is a gap in our data, not a weak roster.
 *
 * Ties share a word: a tied group takes the tercile of its first member, so two
 * identical rosters can never be graded differently.
 *
 * Mutates each team in `byTeam`, adding `positionGrades`.
 */
export function assignPositionGrades(byTeam, positionCounts) {
  for (const group of GRADE_POSITIONS) {
    const rows = Object.entries(byTeam).map(([id, t]) => [
      id,
      positionScore(t.matched, group, positionCounts),
    ]);
    const scored = rows.filter(([, s]) => s.matched > 0).sort((a, b) => b[1].score - a[1].score);
    const n = scored.length;
    scored.forEach(([, s]) => {
      const first = scored.findIndex(([, o]) => o.score === s.score);
      s.grade = GRADE_WORDS[Math.min(GRADE_WORDS.length - 1, Math.floor((first * 3) / n))];
    });
    for (const [id, s] of rows) {
      if (!byTeam[id].positionGrades) byTeam[id].positionGrades = {};
      byTeam[id].positionGrades[group] = {
        score: s.score,
        matched: s.matched,
        k: s.k,
        grade: s.grade || null,
      };
    }
  }
  return byTeam;
}

/**
 * Project every team in a /rate report.
 *
 * @param report          the report object from /api/rate-league
 * @param scoringSettings raw Sleeper scoring_settings (the API pass-through)
 * @returns null when scoring is missing — the page omits the stat rather than
 *          projecting under a lens the league never chose — else
 *          { byTeam: { [rosterId]: { ...projectTeam, rank, positionGrades } },
 *            teamCount }
 */
export function projectRoom(report, scoringSettings) {
  if (!scoringSettings || !report || !Array.isArray(report.leaderboard)) return null;
  const lens = scoringFromSleeper(scoringSettings);
  const positions = report.positions || {};

  const byTeam = {};
  for (const team of report.leaderboard) {
    byTeam[team.rosterId] = projectTeam(team.picks, positions, lens);
  }

  assignPositionGrades(byTeam, positions);

  // Rank by week1, highest first; ties share a rank (1, 2, 2, 4).
  const ordered = Object.entries(byTeam).sort((a, b) => b[1].week1 - a[1].week1);
  ordered.forEach(([, t], i) => {
    const prev = i > 0 ? ordered[i - 1][1] : null;
    t.rank = prev && prev.week1 === t.week1 ? prev.rank : i + 1;
  });

  return { byTeam, teamCount: ordered.length };
}
